import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useHowItWorks} from 'hooks/useHowItWorks'
import {getLanguageCode} from 'modules/meta/selectors'
import {getShowHowToCancelLink} from 'modules/searchApi/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Brand} from '@vio/core/src/brand/types'
import {
  getLocalizedContactUrl,
  getLocalizedFooterUrl,
  getLocalizedHelpUrl
} from '@vio/core/src/brand/utils'
import {
  FooterLink,
  FooterLinkProps,
  useFooterLegalLinks
} from '@vio/shared/src/whiteLabel/CustomFooter'

import {messages} from './constants'

const UnstyledList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${cssTheme.layout.spacing.s300};
`

interface LinkSectionProps {
  title: string
  links: FooterLinkProps[]
}
const LinkSection = ({title, links}: LinkSectionProps) => {
  const validLinks = useMemo(
    () => links.filter(link => link && (link.href || link.onClick)),
    [links]
  )

  if (!validLinks.length) {
    return null
  }
  return (
    <div>
      <Text variant="titleL" tag="p">
        {title}
      </Text>
      <Box paddingTop="s400">
        <UnstyledList>
          {validLinks.map(link => (
            <li key={link.label}>
              <FooterLink {...link} />
            </li>
          ))}
        </UnstyledList>
      </Box>
    </div>
  )
}

interface Props {
  brand: Brand
}
export const FooterLinksSection = ({brand}: Props) => {
  const legalLinks = useFooterLegalLinks(brand)
  const {formatMessage} = useIntl()
  const languageCode = useSelector(getLanguageCode)
  const showHowToCancel = useSelector(getShowHowToCancelLink)
  const {isEnabled: isHowItWorksEnabled, link: howItWorksLink} = useHowItWorks()

  return (
    <>
      <LinkSection
        title={formatMessage(messages.productTitle)}
        links={[
          {
            label: formatMessage(messages.affiliateProgram),
            href: brand.links.developersUrl
          },
          ...legalLinks
        ]}
      />
      <LinkSection
        title={formatMessage(messages.companyTitle)}
        links={[
          {
            label: formatMessage(messages.aboutUs),
            href: brand.links.aboutUsUrl
          },
          {
            label: formatMessage(messages.blog),
            href: brand.links.blogUrl
          },
          {
            label: formatMessage(messages.careers),
            href: brand.links.careersUrl
          },
          isHowItWorksEnabled && {
            label: formatMessage(brand.messages.howItWorksMessage, {
              brandName: brand.name
            }),
            href: howItWorksLink
          }
        ]}
      />
      <LinkSection
        title={formatMessage(messages.contactTitle)}
        links={[
          {
            label: formatMessage(messages.getHelp),
            href: getLocalizedHelpUrl(brand, languageCode)
          },
          {
            label: formatMessage(messages.partnerships),
            href: brand.links.partnershipsUrl
          },
          {
            label: formatMessage(messages.contactUs),
            href: getLocalizedContactUrl(brand, languageCode)
          },
          showHowToCancel &&
            brand.links.howToCancelUrl && {
              label: formatMessage(messages.howToCancel),
              href: getLocalizedFooterUrl(
                brand.links.howToCancelUrl,
                languageCode
              )
            }
        ]}
      />
    </>
  )
}
